"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { TypeSelect } from "@/components/type-select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useCreateLink } from "@/hooks/use-create-link"
import { toast } from "sonner"
import { Copy, Check, Loader2, Link2 } from "lucide-react"

export function QuickLinkGenerator() {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState("")
  const [tag, setTag] = useState("")
  const [note, setNote] = useState("")
  const [utmSource, setUtmSource] = useState("")
  const [utmMedium, setUtmMedium] = useState("")
  const [utmCampaign, setUtmCampaign] = useState("")
  const [utmContent, setUtmContent] = useState("")
  const [generatedLink, setGeneratedLink] = useState("")
  const [copied, setCopied] = useState(false)

  const { mutate: createLink, isPending } = useCreateLink()

  const resetForm = () => {
    setType("")
    setTag("")
    setNote("")
    setUtmSource("")
    setUtmMedium("")
    setUtmCampaign("")
    setUtmContent("")
    setGeneratedLink("")
  }

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault()

    if (!type) {
      toast.error("Select a link type")
      return
    }

    createLink(
      {
        type,
        code: tag.trim() || undefined,
        note: note.trim() || undefined,
        utm_source: utmSource.trim() || undefined,
        utm_medium: utmMedium.trim() || undefined,
        utm_campaign: utmCampaign.trim() || undefined,
        utm_content: utmContent.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          setGeneratedLink(data.link)
          toast.success("Link created")
        },
        onError: (err) => {
          toast.error(err.message || "Failed to create link")
        },
      },
    )
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink)
      setCopied(true)
      toast.success("Link copied!")
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error("Failed to copy link")
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) resetForm()
      }}
    >
      <DialogTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-md bg-gradient-to-r from-amber-500 to-yellow-400 px-4 py-2 text-sm font-semibold text-black shadow-sm transition-all hover:from-amber-400 hover:to-yellow-300 hover:shadow-md"
        >
          <Link2 className="size-4" />
          <span>Quick Link</span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="size-4" />
            Quick Link
          </DialogTitle>
        </DialogHeader>

        {generatedLink ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Generated Link</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm break-all">
                  {generatedLink}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="size-4 text-green-500" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Create Another
              </Button>
              <Button className="flex-1" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleGenerate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type *</Label>
                <TypeSelect value={type} onValueChange={setType} />
              </div>
              <div className="space-y-2">
                <Label>
                  Code{" "}
                  <span className="text-muted-foreground text-xs">
                    (auto if empty)
                  </span>
                </Label>
                <Input
                  placeholder="e.g. summer-2026"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                placeholder="Optional note..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Source</Label>
                <Input
                  placeholder="e.g. twitter"
                  value={utmSource}
                  onChange={(e) => setUtmSource(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Medium</Label>
                <Input
                  placeholder="e.g. social"
                  value={utmMedium}
                  onChange={(e) => setUtmMedium(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Campaign</Label>
                <Input
                  placeholder="e.g. launch-2026"
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Content</Label>
                <Input
                  placeholder="e.g. hero-banner"
                  value={utmContent}
                  onChange={(e) => setUtmContent(e.target.value)}
                />
              </div>
            </div>

            <Button type="submit" disabled={isPending} className="w-full">
              {isPending && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              Generate Link
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
